/* Progressive product previews. Delegated events also cover recently viewed,
   fetched recommendations and replacement filter results. No click interception. */
(() => {
  const mouse = window.matchMedia('(hover: hover) and (pointer: fine)');
  const states = new WeakMap();
  const active = new Set();
  const selector = 'product-card > .media > a > x-grid';

  function frameFor(target) {
    if (!(target instanceof Element)) return null;
    const card = target.closest('product-card');
    const frame = card?.querySelector(selector);
    return frame?.querySelector(':scope > .sm-card-media__layer--secondary img') ? frame : null;
  }

  function stateFor(frame) {
    if (!states.has(frame)) states.set(frame, { hovered: false, focused: false, timer: 0, version: 0, ready: false, pending: null, attempts: 0 });
    return states.get(frame);
  }

  function prepare(frame, state) {
    if (state.ready) return Promise.resolve(true);
    if (state.pending) return state.pending;
    // One later deliberate hover may retry a transient failure. Never loop
    // network requests or hide the front image while a preview is unavailable.
    if (state.attempts >= 2) return Promise.resolve(false);
    const attempt = ++state.attempts;
    const img = frame.querySelector(':scope > .sm-card-media__layer--secondary img');
    const pending = new Promise(resolve => {
      let decoding = false;
      let settled = false;
      let timeout = 0;
      const finish = success => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        img.removeEventListener('load', loaded);
        img.removeEventListener('error', failed);
        state.ready = success;
        resolve(success);
      };
      const failed = () => finish(false);
      const loaded = async () => {
        if (decoding || settled) return;
        if (!img.complete || img.naturalWidth === 0) return finish(false);
        decoding = true;
        try {
          if (typeof img.decode === 'function') await img.decode();
          finish(img.complete && img.naturalWidth > 0);
        } catch (_) {
          finish(false);
        }
      };
      img.addEventListener('load', loaded);
      img.addEventListener('error', failed);
      timeout = window.setTimeout(failed, 8000);
      // Give the explored alternate normal priority without fetching other cards.
      img.fetchPriority = 'auto';
      // Promote only the alternate the shopper deliberately explores. Native
      // auto sizes apply only to lazy images; keep the explicit fallback here.
      if (img.loading === 'lazy') {
        img.sizes = img.sizes.replace(/^auto,\s*/, '');
        img.loading = 'eager';
      }
      // Set responsive sources only for this explored alternate. Preserve CDN
      // parameters, sizes and full-resolution candidates without a placeholder
      // request competing with the primary photograph on first load.
      const deferredSrcset = img.getAttribute('data-srcset');
      const deferredSrc = img.getAttribute('data-src');
      if (deferredSrcset) {
        img.setAttribute('srcset', deferredSrcset);
        img.removeAttribute('data-srcset');
      }
      if (deferredSrc) {
        img.setAttribute('src', deferredSrc);
        img.removeAttribute('data-src');
      }
      if (attempt > 1 && img.complete && img.naturalWidth === 0) {
        // Reassign the original responsive sources to retry a broken request;
        // retain the CDN URL, responsive selection and browser cache policy.
        const src = img.getAttribute('src');
        const srcset = img.getAttribute('srcset');
        if (srcset) img.setAttribute('srcset', srcset);
        if (src) img.setAttribute('src', src);
      } else if (img.complete) loaded();
    });
    state.pending = pending.then(success => {
      state.pending = null;
      return success;
    });
    return state.pending;
  }

  function update(frame, delay = 0) {
    const state = stateFor(frame);
    clearTimeout(state.timer);
    const version = ++state.version;
    if (!state.hovered && !state.focused) {
      frame.removeAttribute('data-sm-preview');
      active.delete(frame);
      return;
    }
    active.add(frame);
    // Load and decode during the brief intent window, not after it. The front
    // stays in place until both readiness and the current hover are confirmed.
    const prepared = prepare(frame, state);
    state.timer = window.setTimeout(async () => {
      const ready = await prepared;
      if (ready && version === state.version && frame.isConnected && (state.hovered || state.focused)) {
        frame.setAttribute('data-sm-preview', 'true');
      }
    }, delay);
  }

  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse' || !mouse.matches) return;
    const frame = frameFor(event.target);
    if (!frame || frame.closest('product-card').contains(event.relatedTarget)) return;
    stateFor(frame).hovered = true;
    update(frame, 20);
  }, { passive: true });

  document.addEventListener('pointerout', event => {
    if (event.pointerType !== 'mouse') return;
    const frame = frameFor(event.target);
    if (!frame || frame.closest('product-card').contains(event.relatedTarget)) return;
    stateFor(frame).hovered = false;
    update(frame);
  }, { passive: true });

  document.addEventListener('focusin', event => {
    const frame = frameFor(event.target);
    if (!frame) return;
    stateFor(frame).focused = event.target.matches(':focus-visible');
    update(frame);
  });

  document.addEventListener('focusout', event => {
    const frame = frameFor(event.target);
    if (!frame) return;
    stateFor(frame).focused = false;
    update(frame);
  });

  function reset() {
    for (const frame of active) {
      const state = stateFor(frame);
      state.hovered = false;
      state.focused = false;
      update(frame);
    }
  }

  // BFCache returns to the front image. Touch taps and carousel swipes never
  // trigger the synthetic CSS hover that used to flash an alternate on click.
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'touch') reset();
  }, { passive: true });
  mouse.addEventListener('change', reset);
  window.addEventListener('pagehide', reset);
  window.addEventListener('pageshow', reset);
  document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });
})();
